import { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AuthContext from "../context/AuthContext";
import useDecks from "../hooks/useDecks";
import DeckPicker from "./decks/DeckPicker";
import { memoryApi } from "../services/api";
import { apiErrorMessage } from "../services/apiError";
import PageHeader from "./PageHeader";

const CreateMemory = ({ onMemoryCreated }) => {
  const { isLoggedIn } = useContext(AuthContext);
  const { decks: availableDecks, loading: loadingDecks } = useDecks();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [selectedDecks, setSelectedDecks] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleDeckToggle = (deckId) => {
    setSelectedDecks((prev) =>
      prev.includes(deckId)
        ? prev.filter((id) => id !== deckId)
        : [...prev, deckId],
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!isLoggedIn) {
      setError("You must be logged in to create a memory.");
      setLoading(false);
      return;
    }

    // Prepare payload - only include fields the API expects for creation
    const payload = {
      question,
      answer,
      decks: selectedDecks,
    };

    try {
      const response = await memoryApi.create(payload);
      setSuccess("Memory created successfully!");
      // Clear form
      setQuestion("");
      setAnswer("");
      setSelectedDecks([]);
      // Notify parent component if needed
      if (onMemoryCreated) {
        onMemoryCreated(response.data); // Pass the newly created memory data
      }
    } catch (err) {
      console.error("Error creating memory:", err.response || err);
      let errorMessage =
        "Failed to create memory. Please check the console for details.";
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (err.response.status === 400) {
          // Specific DRF validation errors (shared extractor handles the shapes,
          // incl. bare-string field values that the old `.join` would throw on).
          errorMessage = apiErrorMessage(err, errorMessage);
        } else {
          errorMessage = `Server error: ${err.response.status}. Please try again later.`;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    // Optionally render nothing or a login prompt if user is not logged in
    return null;
  }

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Create New Memory"
        subtitle="Add a new flashcard to your knowledge base"
      />
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4 grid gap-1.5">
            <Label htmlFor="memory-question" className="font-semibold">
              Question <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="memory-question"
              rows={3}
              className="max-h-48"
              placeholder="What question do you want to remember? (e.g., 'What is the capital of France?')"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              required
            />
            <p className="text-sm text-muted-foreground">
              Enter the question or prompt you want to study
            </p>
          </div>

          <div className="mb-4 grid gap-1.5">
            <Label htmlFor="memory-answer" className="font-semibold">
              Answer <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="memory-answer"
              rows={4}
              className="max-h-48"
              placeholder="What is the correct answer or response? (e.g., 'Paris')"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              required
            />
            <p className="text-sm text-muted-foreground">
              Enter the answer or explanation you want to remember
            </p>
          </div>

          <div className="mb-4 grid gap-1.5">
            <Label id="create-memory-decks-label" className="font-semibold">
              Decks{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <DeckPicker
              decks={availableDecks}
              loading={loadingDecks}
              selected={selectedDecks}
              onToggle={handleDeckToggle}
              labelledBy="create-memory-decks-label"
            />
            <p className="text-sm text-muted-foreground">
              Tap a deck to add this card to it, tap again to take it out.
            </p>
          </div>

          <div className="mx-auto max-w-sm">
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading || !question.trim() || !answer.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Creating Memory...
                </>
              ) : (
                <>💾 Create Memory</>
              )}
            </Button>
          </div>

          <div className="mt-3 text-center">
            <p className="text-sm text-muted-foreground">
              {question.trim() && answer.trim()
                ? "Ready to create memory"
                : "✏️ Fill in both question and answer to continue"}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

CreateMemory.propTypes = {
  onMemoryCreated: PropTypes.func,
};

export default CreateMemory;
